// BL-408 Multi-Company Sign-In, G2: where a person with two or more companies lands after signing in.
// The server decides everything (migrations 250 and 252): mcs_my_companies lists only the caller's own companies,
// mcs_door_company says which of them a company door opens (NULL for anything else, never revealing a company exists),
// and mcs_set_active_company enters a company after checking membership and MFA by role.
// People with one company never get here: every helper returns { kind: 'single' } for them and nothing else happens.
import { listMyCompanies, setActiveCompany } from './activeCompany';

// Browser storage that belongs to ONE company. Cleared on every company change so nothing from the last company is shown
// in the next one. Personal things (theme, dashboard layout, sign-in door brand) stay: layouts are personal (Q-007).
export const COMPANY_LOCAL_KEYS = Object.freeze([
    'intelAgentTenantName',
    'intelAgentActiveBrand',
    'intelAgentScheduleCalendarLabel',
    'intelAgentChartOfAccounts',
    'intelAgentAppointments',
]);
export const COMPANY_SESSION_KEYS = Object.freeze(['intelAgentActiveSupportGrant']);

export function clearCompanyCaches({ local = window.localStorage, session = window.sessionStorage } = {}) {
    for (const key of COMPANY_LOCAL_KEYS) {
        try { local?.removeItem(key); } catch (error) { /* storage blocked: nothing cached to clear */ }
    }
    for (const key of COMPANY_SESSION_KEYS) {
        try { session?.removeItem(key); } catch (error) { /* storage blocked: nothing cached to clear */ }
    }
}

// The company a door opens for this person, or null (not theirs, unknown, or any failure: fail closed).
export async function doorCompany(client, slug) {
    if (!slug) return null;
    try {
        const { data, error } = await client.rpc('mcs_door_company', { p_slug: slug });
        if (error || typeof data !== 'string') return null;
        return data;
    } catch (error) {
        return null;
    }
}

// Decide the landing. Returns one of:
//   { kind: 'single' }                       - 0 or 1 company: today's sign-in, unchanged.
//   { kind: 'active', orgId, companies }     - this sign-in session already chose a company (e.g. a reload).
//   { kind: 'entered', orgId, companies }    - the company door opened one of their companies.
//   { kind: 'platform', companies }          - platform role holder at the platform door: the platform landing.
//   { kind: 'choose', companies, notice }    - "Choose a company" (notice 'mfa_required' when the door's company needs MFA).
// listMyCompanies failing is reported as 'single', so a server hiccup never changes a one-company person's sign-in.
export async function planCompanyLanding({ client, door, isPlatform = false }) {
    let companies;
    try {
        companies = await listMyCompanies(client);
    } catch (error) {
        return { kind: 'single' };
    }
    if (companies.length < 2) return { kind: 'single' };
    const active = companies.find((company) => company.isActive);
    if (active) return { kind: 'active', orgId: active.orgId, companies };
    if (door?.slug) {
        const orgId = await doorCompany(client, door.slug);
        if (orgId) {
            const result = await setActiveCompany(client, orgId);
            if (result.ok) return { kind: 'entered', orgId, companies };
            return { kind: 'choose', companies, notice: result.reason === 'mfa_required' ? 'mfa_required' : null };
        }
    }
    if (!door?.slug && isPlatform) return { kind: 'platform', companies };
    return { kind: 'choose', companies, notice: null };
}

// Enter a company from the chooser (or, in G3, the switcher), then reload cleanly so no screen, cache or live
// subscription from the previous company survives. Resolves to the setActiveCompany result; reloads only on success.
export async function enterCompany(client, company, {
    local = window.localStorage,
    session = window.sessionStorage,
    reload = (path) => window.location.assign(path),
    landingPath = '/',
    // G3: close live subscriptions (Supabase realtime channels) before the reload so none can deliver the old company.
    beforeReload = null,
} = {}) {
    const result = await setActiveCompany(client, company?.orgId);
    if (!result.ok) return result;
    clearCompanyCaches({ local, session });
    if (company?.name) {
        try { local?.setItem('intelAgentTenantName', company.name); } catch (error) { /* storage blocked */ }
    }
    try { await beforeReload?.(); } catch (error) { /* the reload ends them anyway */ }
    reload(landingPath);
    return result;
}

// G3b: accept a verified invitation into ANOTHER company for someone who already holds at least one. Used on the platform
// sign-in path (platform identities never run the tenant resolver). The server decides everything: accept_org_invite
// joins only with an open invite for this email, a finished invite code and MFA by role; without MFA it waits quietly.
// Resolves to true when it asked the server to accept, false otherwise; never throws, never signs anyone out.
export async function acceptPendingCompanyInvite(client) {
    try {
        const companies = await listMyCompanies(client);
        if (companies.length === 0) return false;
        const { data: access, error } = await client.rpc('bl391_app_access');
        if (error || access?.invitation_state !== 'pending') return false;
        const { error: acceptError } = await client.rpc('accept_org_invite');
        return !acceptError;
    } catch (error) {
        return false;
    }
}
